/**
 * Le sezioni della scheda di un progetto sul telefono (7/10/2026): sono le
 * linguette del computer (Task, Timeline, Documenti, Diario, Gestione), che
 * qui si aprono dalla Panoramica e si chiudono con la freccia in alto.
 *
 * Ognuna ha la sua forma, perché risponde a una domanda diversa:
 *   Task       "cosa c'è ancora da fare?"   una lista da spuntare, per stato
 *   Timeline   "quando sono le tappe?"      le milestone in ordine, con la data
 *   Documenti  "dov'è quel file?"           le cartelle in alto, un file per riga
 *   Diario     "cosa ci siamo detti?"       i messaggi, con il cliente e interni
 *   Gestione   "come stiamo con soldi e ore?" l'incassato in grande, poi righe
 * Stesse chiamate e stesse chiavi di cache delle linguette del computer.
 * Quello che dal telefono non si fa (il Gantt, la griglia delle ore, i nuovi
 * preventivi e fatture) lo dice una riga onesta.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, API_BASE_URL } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { fmtEur, calcMargin } from '../../lib/utils';
import { Icon, type IconName } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MSearch, MSeg, iniziali } from '../../components/mobile/MobileKit';
import type { ProjectFull } from './ProjectDetailPage';
import { dataInTesta, giorniA } from './ProgettiMobile';
import {
  ComunicazioneSheet, RigaAzione, RigaComunicazione, RigaTask, TitoloSezione, eInterna, giornoBreve, ordinaTask, personaBreve,
  type Comunicazione,
} from './pjmPezzi';

const euro = (n: number) => `€ ${fmtEur(n)}`;
const allaData = (iso: string) => `${iso.slice(0, 10)}T00:00:00Z`;
const piu = (n: number, uno: string, molti: string) => `${n} ${n === 1 ? uno : molti}`;

// =====================================================================
// TASK — "cosa c'è ancora da fare?"
// =====================================================================

const GRUPPI_TASK: Array<{ k: string; titolo: string }> = [
  { k: 'BLOCKED', titolo: 'Bloccati' },
  { k: 'IN_PROGRESS', titolo: 'In corso' },
  { k: 'REVIEW', titolo: 'In revisione' },
  { k: 'TODO', titolo: 'Da fare' },
];

export function SezioneTask({ p }: { p: ProjectFull }) {
  const [chi, setChi] = useState('');
  const [priorita, setPriorita] = useState('');
  const [tag, setTag] = useState('');
  const [filtri, setFiltri] = useState(false);
  const [fatti, setFatti] = useState(false);

  // Gli stessi tre filtri della barra del computer (assegnatario, priorità, tag).
  const persone = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of p.tasks) if (t.assignee) m.set(t.assignee.id ?? t.assignee.initials, `${t.assignee.firstName} ${t.assignee.lastName}`);
    return [...m.entries()].map(([id, nome]) => ({ id, nome }));
  }, [p.tasks]);
  const tags = useMemo(() => [...new Set(p.tasks.map((t) => t.tag).filter(Boolean))] as string[], [p.tasks]);
  const filtrati = p.tasks.filter((t) => {
    if (chi && t.assignee?.id !== chi && t.assignee?.initials !== chi) return false;
    if (priorita && t.priority !== priorita) return false;
    if (tag && t.tag !== tag) return false;
    return true;
  });
  const accesi = [chi, priorita, tag].filter(Boolean).length;
  const chiusi = filtrati.filter((t) => t.status === 'DONE');
  const aperti = filtrati.length - chiusi.length;

  const voce = (on: boolean, label: string, onClick: () => void) => (
    <button key={label} type="button" role="radio" aria-checked={on} className="m-sheet-row m-act-row" onClick={onClick}>
      <span className="m-sheet-row-lbl">{label}</span>
      {on && <Icon name="check" size={16} />}
    </button>
  );

  return (
    <MPage>
      <TitoloSezione p={p} titolo="Task" sotto={`${piu(aperti, 'da fare', 'da fare')} · ${piu(chiusi.length, 'fatto', 'fatti')}`} />

      {(persone.length > 0 || tags.length > 0) && (
        <div className="pjm-filtri-riga">
          <button type="button" className="m-filter-btn" onClick={() => setFiltri(true)} aria-haspopup="dialog" aria-expanded={filtri}>
            <Icon name="filter" size={14} />
            <span>Filtra</span>
            {accesi > 0 && <span className="n">{accesi}</span>}
          </button>
        </div>
      )}

      {filtrati.length === 0 ? (
        <MGroup title="Task">
          <MEmpty>{accesi ? 'Nessun task con questi filtri.' : 'Nessun task in questo progetto: si aggiungono con il tasto qui sotto.'}</MEmpty>
          {accesi > 0 && <MMore label="Togli i filtri" onClick={() => { setChi(''); setPriorita(''); setTag(''); }} />}
        </MGroup>
      ) : (
        <>
          {GRUPPI_TASK.map((g) => {
            const righe = filtrati.filter((t) => t.status === g.k).sort(ordinaTask);
            if (!righe.length) return null;
            return <MGroup key={g.k} title={g.titolo} count={righe.length}>{righe.map((t) => <RigaTask key={t.id} t={t} senzaStato />)}</MGroup>;
          })}
          {/* Un task con uno stato che non conosco non sparisce: va in fondo. */}
          {(() => {
            const altri = filtrati.filter((t) => t.status !== 'DONE' && !GRUPPI_TASK.some((g) => g.k === t.status));
            return altri.length ? <MGroup title="Altri" count={altri.length}>{altri.map((t) => <RigaTask key={t.id} t={t} />)}</MGroup> : null;
          })()}
          {chiusi.length > 0 && (
            <MGroup title="Fatti" count={chiusi.length}>
              {fatti && chiusi.map((t) => <RigaTask key={t.id} t={t} senzaStato />)}
              <MMore
                label={fatti ? 'Nascondi i task fatti' : chiusi.length === 1 ? 'Mostra il task fatto' : `Mostra i ${chiusi.length} task fatti`}
                onClick={() => setFatti((v) => !v)}
              />
            </MGroup>
          )}
        </>
      )}

      <MobileSheet
        open={filtri}
        onClose={() => setFiltri(false)}
        title="Filtra i task"
        footer={<button className="btn btn-primary m-block" onClick={() => setFiltri(false)}>Vedi i task</button>}
      >
        <div className="pjm-sheet">
          {persone.length > 0 && (
            <div className="m-sheet-group" role="radiogroup" aria-label="Chi lo fa">
              <div className="m-sheet-group-label">Chi lo fa</div>
              {voce(!chi, 'Tutti', () => setChi(''))}
              {persone.map((x) => voce(chi === x.id, x.nome, () => setChi(x.id)))}
            </div>
          )}
          <div className="m-sheet-group" role="radiogroup" aria-label="Priorità">
            <div className="m-sheet-group-label">Priorità</div>
            {voce(!priorita, 'Tutte le priorità', () => setPriorita(''))}
            {voce(priorita === 'ALTA', 'Solo priorità alta', () => setPriorita('ALTA'))}
            {voce(priorita === 'MEDIA', 'Solo priorità media', () => setPriorita('MEDIA'))}
            {voce(priorita === 'BASSA', 'Solo priorità bassa', () => setPriorita('BASSA'))}
          </div>
          {tags.length > 0 && (
            <div className="m-sheet-group" role="radiogroup" aria-label="Tag">
              <div className="m-sheet-group-label">Tag</div>
              {voce(!tag, 'Tutti i tag', () => setTag(''))}
              {tags.map((x) => voce(tag === x, x, () => setTag(x)))}
            </div>
          )}
        </div>
      </MobileSheet>
    </MPage>
  );
}

// =====================================================================
// TIMELINE — "quando sono le tappe?"
// =====================================================================

export function SezioneTimeline({ p }: { p: ProjectFull }) {
  const [dipendenze, setDipendenze] = useState(false);
  const ordinate = [...p.milestones].sort((a, b) => a.order - b.order);
  const aperte = ordinate.filter((m) => !m.done);
  const prossima = aperte[0];
  const quando = (iso: string) => {
    const g = giorniA(allaData(iso));
    return g < 0 ? `${piu(-g, 'giorno', 'giorni')} di ritardo` : g === 0 ? 'oggi' : g === 1 ? 'domani' : `fra ${g} giorni`;
  };
  return (
    <MPage>
      <TitoloSezione
        p={p}
        titolo="Timeline"
        sotto={prossima ? `Prossima tappa: ${prossima.label}, ${quando(prossima.dueDate)}` : ordinate.length ? 'Tutte le milestone sono fatte.' : undefined}
      />

      <MGroup title="Milestone" count={ordinate.length || undefined}>
        {ordinate.length ? ordinate.map((m) => {
          const g = giorniA(allaData(m.dueDate));
          return (
            <MRow
              key={m.id}
              text={dataInTesta(allaData(m.dueDate))}
              tone={!m.done && g < 0 ? 'alert' : undefined}
              title={m.done ? <span className="pjm-barrato">{m.label}</span> : m.label}
              sub={m.done ? 'fatta' : `${m.active ? 'in corso · ' : ''}${quando(m.dueDate)}`}
            />
          );
        }) : <MEmpty>Nessuna milestone: si aggiungono con il tasto qui sotto.</MEmpty>}
      </MGroup>

      {ordinate.length > 1 && (
        <MGroup title="Dipendenze" note="Ogni tappa comincia quando finisce quella prima (fine-inizio).">
          {dipendenze && ordinate.slice(0, -1).map((m, i) => (
            <MRow key={m.id} icon="arrowRight" title={`${m.label} → ${ordinate[i + 1]!.label}`} sub={m.active ? 'in corso' : m.done ? 'fatta' : 'ok'} />
          ))}
          <MMore label={dipendenze ? 'Nascondi le dipendenze' : `Mostra le ${ordinate.length - 1} dipendenze`} onClick={() => setDipendenze((v) => !v)} />
        </MGroup>
      )}

      <DesktopOnly
        cosa="Il Gantt delle milestone"
        perche="è una fascia lunga da leggere di traverso, e su uno schermo stretto le barre diventano trattini. Qui le milestone sono in elenco, in ordine."
      ><></></DesktopOnly>
    </MPage>
  );
}

// =====================================================================
// DOCUMENTI — "dov'è quel file?"
// =====================================================================

const CARTELLE: Array<{ k: string; label: string }> = [
  { k: 'CONTRATTI', label: 'Contratti' },
  { k: 'BRIEF', label: 'Brief e specifiche' },
  { k: 'DESIGN', label: 'Design' },
  { k: 'DELIVERY', label: 'Consegne' },
  { k: 'ALTRO', label: 'Altro' },
];

type Documento = ProjectFull['documents'][number];

export function SezioneDocumenti({ p }: { p: ProjectFull }) {
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);
  const [cartella, setCartella] = useState('tutti');
  const [search, setSearch] = useState('');
  const [aperto, setAperto] = useState<Documento | null>(null);

  const scarica = async (d: Documento) => {
    // Lo stesso giro della scheda del computer: il file arriva col token, e
    // si salva con il suo nome.
    const res = await fetch(`${API_BASE_URL}/documents/${d.id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) { toast.alert('Download non riuscito', 'Riprova tra un momento.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = d.name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const elimina = useMutation({
    mutationFn: (id: string) => api.delete(`/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', p.id] }),
  });

  const conta = (k: string) => p.documents.filter((d) => d.docGroup === k).length;
  const q = search.trim().toLowerCase();
  const visibili = p.documents.filter((d) => (cartella === 'tutti' || d.docGroup === cartella) && (!q || d.name.toLowerCase().includes(q)));
  const opzioni = [
    { key: 'tutti', label: 'Tutti', count: p.documents.length },
    ...CARTELLE.filter((c) => conta(c.k) > 0 || cartella === c.k).map((c) => ({ key: c.k, label: c.label, count: conta(c.k) })),
  ];
  const nomeCartella = CARTELLE.find((c) => c.k === cartella)?.label ?? 'Tutti i documenti';
  const dettaglio = (d: Documento) => [
    d.mime.split('/')[1]?.toUpperCase().slice(0, 5),
    d.version ?? 'v1',
    giornoBreve(d.createdAt),
    personaBreve(d.uploader),
    d.size ? `${Math.max(1, Math.round(d.size / 1024))} KB` : null,
  ].filter(Boolean).join(' · ');

  return (
    <MPage>
      <TitoloSezione p={p} titolo="Documenti" sotto={p.documents.length ? piu(p.documents.length, 'documento', 'documenti') : undefined} />

      {p.documents.length > 0 && (
        <div className="pjm-tools-wrap">
          {opzioni.length > 2 && <MSeg value={cartella} onChange={setCartella} options={opzioni} />}
          {p.documents.length > 6 && <MSearch value={search} onChange={setSearch} placeholder="Cerca un documento" />}
        </div>
      )}

      <MGroup title={cartella === 'tutti' ? 'Tutti i documenti' : nomeCartella} count={visibili.length || undefined}>
        {visibili.length
          ? visibili.map((d) => <MRow key={d.id} icon="file" title={d.name} sub={dettaglio(d)} onClick={() => setAperto(d)} />)
          : <MEmpty>{q ? `Nessun documento con "${search.trim()}".` : p.documents.length ? 'Nessun documento in questa cartella.' : 'Nessun documento: si caricano con il tasto qui sotto.'}</MEmpty>}
      </MGroup>

      <MobileSheet open={!!aperto} onClose={() => setAperto(null)} title={aperto?.name}>
        {aperto && (
          <div className="pjm-sheet">
            <div className="pjm-meta">{CARTELLE.find((c) => c.k === aperto.docGroup)?.label ?? aperto.docGroup} · {dettaglio(aperto)}</div>
            <button className="btn btn-primary m-block" onClick={() => scarica(aperto)}><Icon name="export" size={12} /> Scarica</button>
            <RigaAzione
              icon="trash"
              label="Elimina il documento"
              danger
              onClick={async () => {
                const d = aperto;
                setAperto(null);
                if (await confirmDialog({ title: `Eliminare "${d.name}"?`, danger: true, confirmLabel: 'Elimina documento' })) elimina.mutate(d.id);
              }}
            />
          </div>
        )}
      </MobileSheet>
    </MPage>
  );
}

// =====================================================================
// DIARIO — "cosa ci siamo detti?"
// =====================================================================

const ICONA_ATTIVITA: Record<string, IconName> = { TASK: 'check', ALERT: 'alert', DOC: 'file', BUDGET: 'euro', COMM: 'mail', COMMIT: 'link', QA: 'checklist' };

export function SezioneDiario({ p, iniziale }: { p: ProjectFull; iniziale: 'comunicazioni' | 'attivita' }) {
  const [vista, setVista] = useState<'comunicazioni' | 'attivita'>(iniziale);
  const [comm, setComm] = useState<Comunicazione | null>(null);
  const [tutteAtt, setTutteAtt] = useState(false);
  const { data: stats } = useQuery({
    queryKey: ['project-stats', p.id],
    queryFn: () => api.get<{ daysActive: number; activitiesLast4w: number; commsTotal: number; emails: number; meetings: number; tasksDone: number; tasksOpen: number }>(`/projects/${p.id}/stats`),
    enabled: vista === 'attivita',
  });

  const ordinate = [...p.communications].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  const conCliente = ordinate.filter((c) => !eInterna(c));
  const interne = ordinate.filter(eInterna);
  const attivita = [...p.activity].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const visibiliAtt = tutteAtt ? attivita : attivita.slice(0, 15);

  return (
    <MPage>
      <TitoloSezione p={p} titolo="Diario" />
      <MSeg
        value={vista}
        onChange={setVista}
        options={[
          { key: 'comunicazioni', label: 'Comunicazioni', count: p.communications.length || undefined },
          { key: 'attivita', label: 'Attività', count: p.activity.length || undefined },
        ]}
      />

      {vista === 'comunicazioni' ? (
        ordinate.length === 0 ? (
          <MGroup title="Comunicazioni">
            <MEmpty>Nessuna comunicazione: email, chiamate e incontri si scrivono con il tasto qui sotto.</MEmpty>
          </MGroup>
        ) : (
          <>
            {conCliente.length > 0 && (
              <MGroup title="Con il cliente" count={conCliente.length}>
                {conCliente.map((c) => <RigaComunicazione key={c.id} c={c} onApri={() => setComm(c)} />)}
              </MGroup>
            )}
            {interne.length > 0 && (
              <MGroup title="Note interne" count={interne.length}>
                {interne.map((c) => <RigaComunicazione key={c.id} c={c} onApri={() => setComm(c)} />)}
              </MGroup>
            )}
          </>
        )
      ) : (
        <>
          <MGroup title="Cosa è successo" count={attivita.length || undefined}>
            {visibiliAtt.length ? visibiliAtt.map((a) => (
              <MRow
                key={a.id}
                icon={ICONA_ATTIVITA[a.kind] ?? 'pin'}
                tone={a.kind === 'ALERT' ? 'alert' : undefined}
                title={a.message}
                // Con l'Assistente (28/10/2026) la persona resta, «tramite MUNE».
                sub={[
                  a.meta?.origine === 'assistente'
                    ? (a.actor ? `${a.actor.firstName} ${a.actor.lastName}, tramite MUNE` : 'MUNE')
                    : (a.actor ? `${a.actor.firstName} ${a.actor.lastName}` : 'Sistema'),
                  giornoBreve(a.createdAt),
                ].join(' · ')}
              />
            )) : <MEmpty>Nessuna attività: le azioni sul progetto compaiono qui da sole.</MEmpty>}
            {attivita.length > 15 && <MMore label={tutteAtt ? 'Mostra meno' : `Vedi tutte le ${attivita.length}`} onClick={() => setTutteAtt((v) => !v)} />}
          </MGroup>

          {stats && (
            <MGroup title="Il progetto in numeri">
              <MRow title="Giorni di lavoro" trail={stats.daysActive} />
              <MRow title="Attività nelle ultime 4 settimane" trail={stats.activitiesLast4w} />
              <MRow title="Task fatti" sub={`${stats.tasksOpen} ancora aperti`} trail={stats.tasksDone} />
              <MRow title="Comunicazioni" sub={`${piu(stats.emails, 'email', 'email')}, ${piu(stats.meetings, 'incontro', 'incontri')} con il cliente`} trail={stats.commsTotal} />
            </MGroup>
          )}

          {!!p.automations?.length && (
            <MGroup title="Automazioni">
              {p.automations.map((a) => <MRow key={a.id} icon="zap" title={a.name} trail={a.enabled ? 'attiva' : 'spenta'} trailTone="mute" />)}
            </MGroup>
          )}
        </>
      )}

      <ComunicazioneSheet projectId={p.id} c={comm} onClose={() => setComm(null)} />
    </MPage>
  );
}

// =====================================================================
// GESTIONE — "come stiamo con soldi e ore?"
// =====================================================================

const CATEGORIE_COSTO: Record<string, string> = {
  SVILUPPO: 'Sviluppo', DESIGN: 'Design', PM: 'Project management', PROFESSIONISTI: 'Professionisti', QA: 'QA e strumenti',
  INFRASTRUTTURA: 'Infrastruttura', TOOLS: 'Strumenti', DOCENTI: 'Docenti', RECRUITER: 'Recruiter', ADV: 'Pubblicità', FORN: 'Fornitori', ALTRO: 'Altro',
};

const STATO_DOC: Record<string, string> = {
  BOZZA: 'bozza', INVIATO: 'inviato', VISUALIZZATO: 'visto dal cliente', ACCETTATO: 'accettato', CONVERTITO: 'diventato fattura',
  RIFIUTATO: 'rifiutato', SCADUTO: 'scaduto', EMESSA: 'emessa', PAGATA: 'pagata', SCADUTA: 'scaduta', ANNULLATA: 'annullata',
};
const statoDoc = (s: string) => STATO_DOC[s] ?? s.replace(/_/g, ' ').toLowerCase();

export function SezioneGestione({ p }: { p: ProjectFull }) {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [scenari, setScenari] = useState(false);

  const { data: cf } = useQuery({
    queryKey: ['project-cash-flow', p.id],
    queryFn: () => api.get<{ contractValue: number; collected: number; issued: number; forecast: number; approvedChangeRequests: number }>(`/projects/${p.id}/cash-flow`),
  });
  const { data: team } = useQuery({
    queryKey: ['project-team-progress', p.id],
    queryFn: () => api.get<Array<{ id: string; role: string; isInternal: boolean; user: any; supplier: any; hourlyRate?: number; hoursPlanned: number; spentHours: number; pct: number; cost?: number }>>(`/projects/${p.id}/team-progress`),
  });
  const { data: quotesData } = useQuery({
    queryKey: ['quotes', 'project', p.id],
    queryFn: () => api.get<{ items: Array<{ id: string; number: string | null; title: string | null; status: string; total: string; issueDate: string | null; signedAt: string | null; convertedToInvoice: { id: string; number: string | null } | null }> }>(`/quotes?projectId=${p.id}&pageSize=50`),
  });
  const { data: breakdown } = useQuery({
    queryKey: ['project-cost-breakdown', p.id],
    queryFn: () => api.get<{ total: number; items: Array<{ category: string; amount: number; pct: number }> }>(`/projects/${p.id}/cost-breakdown`),
    enabled: showMargin,
  });

  const contratto = cf?.contractValue ?? p.revenue;
  const incassato = cf?.collected ?? 0;
  const emesso = cf?.issued ?? 0;
  const daEmettere = cf?.forecast ?? 0;
  const pct = (n: number) => (contratto > 0 ? Math.round((n / contratto) * 100) : 0);
  const totaleSal = p.salPayments.reduce((a, s) => a + (Number(s.amount) || 0), 0);
  const quotes = quotesData?.items ?? [];

  const cost = p.cost ?? 0;
  const { abs, pct: pctMargine } = calcMargin(p.revenue, cost);
  const soglia = Number(p.marginThreshold);
  const crApprovate = p.changeRequests.filter((c) => c.status === 'APPROVATA').reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const elencoScenari = [
    { nome: 'Come adesso', revenue: p.revenue, cost, nota: 'il margine di oggi' },
    { nome: 'Con le richieste approvate', revenue: p.revenue + crApprovate, cost, nota: `${euro(crApprovate)} di ricavi in più` },
    { nome: 'Se i costi scendono', revenue: p.revenue, cost: cost * 0.9, nota: 'costi meno 10%' },
    { nome: 'Se i costi crescono un po\'', revenue: p.revenue, cost: cost * 1.1, nota: 'costi più 10%' },
    { nome: 'Se i costi crescono molto', revenue: p.revenue, cost: cost * 1.25, nota: 'costi più 25%' },
  ].map((s) => ({ ...s, ...calcMargin(s.revenue, s.cost) }));
  const costoTeam = (team ?? []).reduce((a, r) => a + (Number(r.cost) || 0), 0);

  return (
    <MPage>
      <TitoloSezione p={p} titolo="Team, ore e conti" />

      {incassato > 0 ? (
        <MHero
          label="Incassato"
          unit="€"
          value={fmtEur(incassato)}
          sub={contratto > 0
            ? `${pct(incassato)}% del contratto da ${euro(contratto)}${daEmettere > 0 ? ` · ${euro(daEmettere)} ancora da fatturare` : ''}`
            : 'il valore del contratto non è ancora scritto'}
        />
      ) : (
        // Un "€ 0" grande non dice niente (mobile.md §12.3): si dice a parole.
        <MHero
          label="Incassato"
          value={<span className="pjm-frase">Niente, per ora</span>}
          sub={contratto > 0 ? `su un contratto da ${euro(contratto)}` : 'il valore del contratto non è ancora scritto'}
        />
      )}

      {/* Le righe a zero non si mostrano: se non c'è niente di fatturato lo dice la nota. */}
      <MGroup
        title="Soldi del progetto"
        note={!incassato && !emesso && !daEmettere ? 'Nessuna fattura è agganciata a questo progetto.' : undefined}
      >
        <MRow icon="euro" title="Valore del contratto" sub={cf && cf.approvedChangeRequests > 0 ? `più ${euro(cf.approvedChangeRequests)} di richieste approvate` : undefined} trail={euro(contratto)} />
        {incassato > 0 && <MRow icon="banknote" title="Incassato" sub={`${pct(incassato)}% del contratto`} trail={euro(incassato)} />}
        {emesso > 0 && <MRow icon="invoice" title="Fatturato, da incassare" sub={`${pct(emesso)}% del contratto`} trail={euro(emesso)} />}
        {daEmettere > 0 && <MRow icon="calendarClock" title="Ancora da fatturare" sub={`${pct(daEmettere)}% del contratto`} trail={euro(daEmettere)} />}
      </MGroup>

      <MGroup
        title="Piano SAL"
        count={p.salPayments.length || undefined}
        action={{ label: 'Aggiungi', onClick: () => openModal('newSAL', { projectId: p.id }) }}
        note={p.salPayments.length ? `In tutto ${euro(totaleSal)}.` : undefined}
      >
        {p.salPayments.length
          ? p.salPayments.map((s) => (
            <MRow key={s.id} icon="coins" title={s.label} sub={[s.dueDate ? `entro il ${giornoBreve(s.dueDate)}` : null, statoDoc(s.status)].filter(Boolean).join(' · ')} trail={euro(Number(s.amount) || 0)} />
          ))
          : <MEmpty>Nessuno stato avanzamento lavori previsto.</MEmpty>}
      </MGroup>

      {p.changeRequests.length > 0 && (
        <MGroup title="Richieste di modifica" count={p.changeRequests.length}>
          {p.changeRequests.map((c) => (
            <MRow key={c.id} icon="swap" title={c.label} sub={[c.date ? giornoBreve(c.date) : null, c.status.replace(/_/g, ' ').toLowerCase()].filter(Boolean).join(' · ')} trail={euro(Number(c.amount) || 0)} />
          ))}
        </MGroup>
      )}

      <MGroup title="Preventivi e fatture" count={quotes.length + p.invoices.length || undefined}>
        {quotes.map((q) => (
          <MRow
            key={q.id}
            icon="file"
            title={q.title ?? 'Proposta'}
            sub={[q.number ? `Preventivo ${q.number}` : 'Preventivo', q.issueDate ? giornoBreve(q.issueDate) : null, statoDoc(q.status)].filter(Boolean).join(' · ')}
            trail={euro(Number(q.total) || 0)}
            onClick={() => navigate(`/quotes/${q.id}`)}
          />
        ))}
        {p.invoices.map((i) => (
          <MRow
            key={i.id}
            icon="invoice"
            title={i.number ? `Fattura ${i.number}` : 'Fattura in bozza'}
            sub={[i.issueDate ? giornoBreve(i.issueDate) : null, statoDoc(i.status)].filter(Boolean).join(' · ')}
            trail={euro(Number(i.total) || 0)}
            trailTone={i.status === 'ANNULLATA' ? 'mute' : undefined}
            onClick={() => navigate(`/invoices/${i.id}`)}
          />
        ))}
        {!quotes.length && !p.invoices.length && <MEmpty>Nessun preventivo e nessuna fattura agganciati a questo progetto.</MEmpty>}
      </MGroup>

      <MGroup
        title="Team"
        count={team?.length || undefined}
        action={{ label: 'Assegna', onClick: () => openModal('assignMember', { projectId: p.id }) }}
      >
        {team?.length ? team.map((r) => {
          const nome = r.user ? `${r.user.firstName} ${r.user.lastName}` : r.supplier?.fullName ?? 'Senza nome';
          // Il carico sul mese: le ore pianificate su 160, come "Carico vs capacità" del computer.
          const carico = Math.round(((Number(r.hoursPlanned) || 0) / 160) * 100);
          return (
            <MRow
              key={r.id}
              text={iniziali(nome)}
              round
              tone={r.pct > 100 ? 'alert' : undefined}
              title={nome}
              sub={[r.role, r.isInternal ? 'interno' : 'esterno', `${Number(r.spentHours).toFixed(1).replace('.0', '')} h di ${r.hoursPlanned}`, `${carico}% del mese`].filter(Boolean).join(' · ')}
              trail={`${r.pct}%`}
              trailSub={showMargin && r.cost != null ? euro(r.cost) : undefined}
            />
          );
        }) : <MEmpty>Nessuno assegnato: le ore e i costi si pianificano assegnando le persone.</MEmpty>}
      </MGroup>

      {showMargin && (
        <>
          <MGroup title="Margine" note="Riservato: ogni accesso a questi numeri resta nel registro.">
            <MRow title="Ricavi" trail={euro(p.revenue)} />
            {cost > 0 && <MRow title="Costi" trail={euro(cost)} />}
            <MRow
              tone={cost > 0 && pctMargine < soglia ? 'alert' : undefined}
              title="Margine lordo"
              sub={cost === 0 ? 'costi da inserire: senza, il margine non si sa' : `${pctMargine.toFixed(1)}% sui ricavi`}
              trail={cost === 0 ? '—' : euro(abs)}
              trailTone={cost === 0 ? 'mute' : cost > 0 && pctMargine < soglia ? 'alert' : undefined}
            />
            <MRow title="Soglia minima" sub={cost === 0 ? 'da verificare' : pctMargine < soglia ? 'sotto soglia' : 'rispettata'} trail={`${soglia.toFixed(0)}%`} />
            {costoTeam > 0 && <MRow title="Costo del team" trail={euro(costoTeam)} />}
          </MGroup>

          {!!breakdown?.items.length && (
            <MGroup title="Costi per categoria" note={`In tutto ${euro(breakdown.total)}.`}>
              {breakdown.items.map((b) => (
                <MRow key={b.category} title={CATEGORIE_COSTO[b.category] ?? b.category} sub={`${b.pct}% dei costi`} trail={euro(b.amount)} />
              ))}
            </MGroup>
          )}

          {cost > 0 && (
            <MGroup title="Scenari">
              {(scenari ? elencoScenari : elencoScenari.slice(0, 2)).map((s) => (
                <MRow
                  key={s.nome}
                  title={s.nome}
                  sub={`${s.nota} · ${s.pct.toFixed(1)}%`}
                  trail={euro(s.abs)}
                  trailTone={s.pct < 20 ? 'alert' : undefined}
                />
              ))}
              <MMore label={scenari ? 'Mostra meno' : 'Mostra gli altri 3 scenari'} onClick={() => setScenari((v) => !v)} />
            </MGroup>
          )}
        </>
      )}

      {/* Una riga sola per tutto quello che resta al computer, in fondo. */}
      <DesktopOnly
        cosa="Fare preventivi e fatture nuovi, guardare la griglia delle ore della settimana o eliminare il progetto"
        perche="sono procedure a più passi e tabelle da leggere di traverso. Qui sopra si consultano i documenti e le ore di ognuno."
      ><></></DesktopOnly>
    </MPage>
  );
}
